import { BellRinging } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useNudges } from "~/api/queries";

/**
 * The ⏰ chip kms's own `NudgeStore` comment promised and never built (§0 C5).
 *
 * It renders nothing at all when the document has no pending reminders — a zero is not news —
 * and it links to the lifecycle section, because a count you cannot act on is decoration.
 *
 * ⚠ The count is **yours**: `nudges.list` is creator-scoped, so this never reveals that a
 * colleague set a reminder on the same document.
 */
export function NudgeChip({ documentId }: { documentId: string }) {
  const { t } = useTranslation();
  const { data: nudges } = useNudges(documentId);
  const pending = (nudges ?? []).filter(
    (nudge) =>
      nudge.status === "scheduled" || nudge.status === "snoozed" || nudge.status === "fired",
  ).length;

  if (pending === 0) {
    return null;
  }

  return (
    <Link
      to="/settings/$section"
      params={{ section: "reminders" }}
      aria-label={t("{{count}} reminders on this document", { count: pending })}
      className="flex h-7 shrink-0 items-center gap-1 rounded-full border border-divider bg-bg-secondary px-2 text-small text-text-secondary hover:border-accent hover:text-accent"
    >
      <BellRinging size={13} aria-hidden />
      {pending}
    </Link>
  );
}
